import { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { useJobCardActions } from '../actions/job-card';
import { JobCard } from '../cards/job-card';
import {
  DeckCardContextProvider,
  SearchAndShuffleContextProvider,
} from '../context';

import {
  ModalPage,
  CardContainer,
  ReminderLabel,
  ReminderLabelDescription,
  ReminderCta,
  CloseButton,
  Content,
} from './card-modal.styled';
import { useModalData } from './use-modal-data';

type CardModalProps = {
  cardId: string;
  show: boolean;
  setShow: (show: boolean) => void;
  onAlertIdUpdate?: (cardId: string, alertId: string | undefined) => void;
};

export const CardModal: React.FC<CardModalProps> = (props) => {
  return (
    <SearchAndShuffleContextProvider
      config={{
        filter: {
          selectLabel: '',
          options: [],
        },
      }}
    >
      <CardModalContent {...props} />
    </SearchAndShuffleContextProvider>
  );
};

export const CardModalContent: React.FC<CardModalProps> = ({
  cardId,
  show,
  setShow,
  onAlertIdUpdate,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { takenCardClickHandler } = useJobCardActions();
  const [newAlertId, setNewAlertId] = useState<string | undefined>('');
  const { jobCardData, alertId, toggleModalReminder } = useModalData(
    cardId || '',
    show
  );

  useEffect(() => {
    if (!show && newAlertId !== '' && onAlertIdUpdate && jobCardData?.id) {
      onAlertIdUpdate(jobCardData.id, newAlertId);
    }
  }, [show, newAlertId]);

  if (!jobCardData) {
    return null;
  }

  const reminderCtaHandler = async (): Promise<void> => {
    const alertId = await toggleModalReminder();
    setNewAlertId(alertId);
  };

  const cardClickHandler = (e: React.MouseEvent<HTMLDivElement>): void => {
    const clickInCard =
      cardRef.current &&
      (e.target === cardRef.current ||
        cardRef.current.contains(e.target as Node));

    if (clickInCard) {
      takenCardClickHandler({ type: 'TakenJob', data: jobCardData }, 0);
    }
  };

  return (
    <ModalPage
      closeButton={{ onClick: () => setShow(false) }}
      isAnimated
      isShown={!!show}
      noGrid
      noMargin
    >
      <Content>
        <DeckCardContextProvider
          card={{ type: 'TakenJob', data: jobCardData }}
          deckIndex={-1}
        >
          <CardContainer onClick={cardClickHandler} ref={cardRef}>
            <JobCard data={{ ...jobCardData, type: 'TakenJob' }} />
          </CardContainer>
        </DeckCardContextProvider>
        <ReminderLabel size="xxlarge">
          <FormattedMessage
            id={
              alertId !== undefined
                ? 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_ENABLED'
                : 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_DISABLED'
            }
          />
        </ReminderLabel>
        <ReminderLabelDescription size="medium" lineHeight="increased">
          <FormattedMessage
            id={
              alertId !== undefined
                ? 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_ENABLED_DESCRIPTION'
                : 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_DISABLED_DESCRIPTION'
            }
          />
        </ReminderLabelDescription>
        <ReminderCta
          size="medium"
          variant={alertId !== undefined ? 'onColor' : 'onColorStrong'}
          onClick={reminderCtaHandler}
        >
          <FormattedMessage
            id={
              alertId !== undefined
                ? 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_ENABLED_CTA'
                : 'SEARCH_AND_SHUFFLE_MODAL_REMINDER_DISABLED_CTA'
            }
          />
        </ReminderCta>
        <CloseButton onClick={() => setShow(false)} size="medium">
          <FormattedMessage id="SEARCH_AND_SHUFFLE_MODAL_CLOSE" />
        </CloseButton>
      </Content>
    </ModalPage>
  );
};
